import { httpsCallable } from 'firebase/functions';
import { cloudFunctions } from '@config/firebase';
import { PropertyInteraction } from '../types/match.types';

export interface LandlordMatchProjection {
  id: string;
  compatibilityScore: number;
  budgetOverlap: {
    min: number;
    max: number;
  };
  sharedCity: string;
  sharedLifestyle: string[];
  isActive: boolean;
}

const call = async <Request, Response>(name: string, data: Request): Promise<Response> => {
  const callable = httpsCallable<Request, Response>(cloudFunctions, name);
  const result = await callable(data);
  return result.data;
};

export const ensureChatMembership = (matchId: string) =>
  call<{ matchId: string }, { authorized: boolean }>('ensureChatMembership', { matchId });

export const sendTextMessage = (matchId: string, text: string) =>
  call<{ matchId: string; kind: 'text'; text: string }, { sent: boolean }>('sendChatMessage', {
    matchId,
    kind: 'text',
    text,
  });

export const sendPropertyMessage = (matchId: string, propertyId: string) =>
  call<{ matchId: string; kind: 'property'; propertyId: string }, { sent: boolean }>('sendChatMessage', {
    matchId,
    kind: 'property',
    propertyId,
  });

export const markChatRead = (matchId: string) =>
  call<{ matchId: string }, { updated: boolean }>('markChatRead', { matchId });

export const trackPropertyView = (matchId: string, propertyId: string) =>
  call<{ matchId: string; propertyId: string }, { added: boolean }>('trackPropertyView', {
    matchId,
    propertyId,
  });

export const likeProperty = (matchId: string, propertyId: string) =>
  call<
    { matchId: string; propertyId: string },
    { interaction: PropertyInteraction }
  >('likeProperty', { matchId, propertyId });

export const createPropertyInquiry = (matchId: string, propertyId: string, message: string) =>
  call<
    { matchId: string; propertyId: string; message: string },
    { inquiry: NonNullable<PropertyInteraction['inquiry']> }
  >('createPropertyInquiry', { matchId, propertyId, message });

export const getPotentialMatches = async (propertyId: string) => {
  const result = await call<
    { propertyId: string },
    { matches: LandlordMatchProjection[] }
  >('getPotentialMatches', { propertyId });
  return result.matches;
};

export const createLandlordInvitation = (propertyId: string, matchId: string) =>
  call<
    { propertyId: string; matchId: string },
    { created: boolean }
  >('createLandlordInvitation', { propertyId, matchId });

// Three-way inquiry chat (landlord + matched tenant pair)
export const openInquiryChat = (inquiryId: string) =>
  call<{ inquiryId: string }, { roomId: string }>('openInquiryChat', { inquiryId });

export const sendInquiryMessage = (inquiryId: string, text: string) =>
  call<{ inquiryId: string; text: string }, { sent: boolean }>('sendInquiryMessage', {
    inquiryId,
    text,
  });

export const markInquiryChatRead = (inquiryId: string) =>
  call<{ inquiryId: string }, { updated: boolean }>('markInquiryChatRead', { inquiryId });